import { EventTimeline, MatrixClient } from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

/** True when the IS row represents an explicitly scheduled meeting (not instant). */
export const isScheduledMeetingRecord = (meeting: MeetingResponse): boolean => meeting.scheduled_at != null;

/** Reads `MESSENGER_CALL_STATE.is_instant` on the call room — set by `useCreateMessengerCall`. */
export const isCallRoomMarkedInstant = (matrixClient: MatrixClient, callRoomId: string): boolean => {
  const room = matrixClient.getRoom(callRoomId);
  if (!room?.getLiveTimeline) return false;
  return (
    room
      .getLiveTimeline()
      ?.getState(EventTimeline.FORWARDS)
      ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '')
      ?.getContent()?.is_instant === true
  );
};

/**
 * True when the messenger timeline shows an open session for this call room
 * (a MEETING_STARTED after the latest MEETING_ENDED, if any).
 */
export const hasOpenMeetingSessionOnMessengerTimeline = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  callRoomId: string
): boolean => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return false;

  let lastStarted = Number.NEGATIVE_INFINITY;
  let lastEnded = Number.NEGATIVE_INFINITY;

  for (const event of messengerRoom.getLiveTimeline().getEvents()) {
    const type = event.getType();
    if (type !== CustomMeedioMatrixEvents.MEETING_STARTED && type !== CustomMeedioMatrixEvents.MEETING_ENDED) {
      continue;
    }
    const content = event.getContent() ?? {};
    if ((content.meeting_room_id as string | undefined) !== callRoomId) continue;
    const ts = event.getTs();
    if (type === CustomMeedioMatrixEvents.MEETING_STARTED) {
      lastStarted = Math.max(lastStarted, ts);
    } else {
      lastEnded = Math.max(lastEnded, ts);
    }
  }

  if (!Number.isFinite(lastStarted)) return false;
  return !Number.isFinite(lastEnded) || lastEnded < lastStarted;
};
